import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment-timezone';

import Appearance from 'styles/Appearance.js';
import Utils from 'files/Utils.js';

const DateDurationPickerField = ({ fieldStyle, format, maxHour, minHour, onChange, overrideAlerts, placeholder, requireInteraction, style, utils, value }) => {

    const timezones = useRef(moment.tz.names());

    const [date, setDate] = useState(null);
    const [time, setTime] = useState(null);
    const [timeOfDay, setTimeOfDay] = useState(null);
    const [timezone, setTimezone] = useState(moment.tz.guess());
    const [times, setTimes] = useState([...new Array(144)].map((_, index) => moment().startOf('day').add(index * (1/12), 'hours').format('h:mm')));

    const onDateChange = date => {

        // update local state with date selection 
        let formatted = date.format('YYYY-MM-DD');
        setDate(formatted);

        // parse date time object with new selection
        onParseDateTime({ date: formatted, time, timeOfDay, timezone });
    }

    const onFieldClick = () => {
        utils.datePicker.show({
            dateTime: false,
            date: date ? moment(date) : moment(),
            onDateChange: onDateChange,
            overrideAlerts: overrideAlerts
        });
    }

    const onFormatDate = () => {
        if(!date) {
            return placeholder || 'Select a date...';
        }
        return moment(date).format(format || 'MMM Do, YYYY');
    }

    const onParseDateTime = ({ date, time, timeOfDay, timezone }) => {

        // prevent change notifications if time values are incomplete
        if(!date || !time || !timeOfDay || !timezone) {
            return;
        }

        // format date with timezone in ISO 8601 format and storing actual timezone info in the string
        let nextDate = moment.tz(`${date} ${time}:00 ${timeOfDay}`, 'YYYY-MM-DD HH:mm:ss A', timezone);
        let formatted = nextDate.format('YYYY-MM-DD HH:mm:ss');

        // notify subscriber of change
        if(typeof(onChange) === 'function') {
            onChange({
                date: formatted,
                timezone: timezone
            });
        }
    }

    const onSetRestrictions = () => {

        // if no hour restrictions are set, show all times
        if(!minHour && !maxHour) {
            return;
        }

        let current = moment().startOf('day').add(minHour || 0, 'hours').unix();
        let max = moment().startOf('day').add(maxHour || 23, 'hours').unix();
        setTimes(() => {
            let times = [];
            while(current <= max) {
                let next = moment.unix(current);
                if(next.format('a') === timeOfDay) {
                    times.push(next.format('h:mm'));
                }
                current = next.add(5, 'minutes').unix();
            }
            return times;
        })
    }

    const getTextColor = () => {
        return date ? Appearance.colors.text() : Appearance.colors.subText();
    }

    const onTimeChange = time => {
        setTime(time);
        onParseDateTime({ date, time, timeOfDay, timezone });
    }

    const onTimezoneChange = tz => {
        setTimezone(tz);
        onParseDateTime({ date, time, timeOfDay, timezone: tz });
    }

    const onTimeOfDayChange = tod => {
        let val = tod?.toUpperCase();
        setTimeOfDay(val);
        onParseDateTime({ date, time, timeOfDay: val, timezone });
    }

    const setupTarget = () => {

        // prepare target dates
        let target = value?.date ? moment.tz(value.date, 'YYYY-MM-DDTHH:mm:ss z', value.timezone) : moment();
        let next = Utils.conformDate(target, 5);

        // set time of day regardless of interaction flag
        let tod = next.format('A');
        setTimeOfDay(tod);

        // prevent date and time set if interaction is required
        if(!value && requireInteraction) {
            return;
        }

        // set date and time
        setDate(next.format('YYYY-MM-DD'));
        setTime(next.format('h:mm'));
        setTimezone(value?.timezone || moment.tz.guess());
    }

    useEffect(() => {
        onSetRestrictions();
    }, [timeOfDay])

    useEffect(() => {
        setupTarget();
    }, [value]);

    return (
        <div
        className={'row p-0 m-0'}
        style={{
            width: '100%',
            ...style
        }}>
            <div className={'col-12 mb-2 p-0'}>
                <div
                onClick={onFieldClick}
                className={`dummy-field ${window.theme} cursor-pointer`}
                style={{
                    alignItems: 'center',
                    backgroundColor: Appearance.colors.textField(),
                    border: `1px solid ${Appearance.colors.divider()}`,
                    borderColor: Appearance.colors.divider(),
                    display: 'flex',
                    flexDirection: 'row',
                    flexGrow: 1,
                    position: 'relative',
                    height: 35
                }}>
                    <span style={{
                        fontSize: 12,
                        fontWeight: 500,
                        color: getTextColor(),
                        width: '100%',
                        flexGrow: 1,
                        ...fieldStyle
                    }}>{onFormatDate()}</span>
                </div>
            </div>
            <div className={'col-12 col-md-3 p-0 pr-md-2'}>
                <select
                className={`custom-select ${window.theme}`}
                value={time || 'Time'}
                onChange={e => {
                    let index = Utils.attributeForKey.select(e, 'id');
                    onTimeChange(times[index]);
                }}
                style={{
                    background: `${Appearance.colors.textField()} url("images/custom-select-list-arrows-${window.theme === 'dark' ? 'white' : 'black'}.png") no-repeat right 0.75rem center`,
                    backgroundSize: '10px',
                    color: time ? Appearance.colors.text() : Appearance.colors.subText(),
                    marginRight: 8
                }}>
                    <option disabled={true}>{'Time'}</option>
                    {times.map((time, index) => {
                        return <option key={index} id={index}>{time}</option>
                    })}
                </select>
            </div>
            <div className={'col-12 col-md-3 p-0 pr-md-2'}>
                <select
                className={`custom-select ${window.theme}`}
                value={timeOfDay ? timeOfDay.toUpperCase() : 'AM'}
                onChange={e => {
                    let text = Utils.attributeForKey.select(e, 'id');
                    onTimeOfDayChange(text);
                }}
                style={{
                    background: `${Appearance.colors.textField()} url("images/custom-select-list-arrows-${window.theme === 'dark' ? 'white' : 'black'}.png") no-repeat right 0.75rem center`,
                    backgroundSize: '10px',
                    marginRight: 8
                }}>
                    {['am', 'pm'].map((text, index) => {
                        return <option key={index} id={text}>{text.toUpperCase()}</option>
                    })}
                </select>
            </div>
            <div className={'col-12 col-md-6 p-0'}>
                <select
                className={`custom-select ${window.theme}`}
                value={timezone}
                onChange={e => {
                    let tz = Utils.attributeForKey.select(e, 'id');
                    onTimezoneChange(tz);
                }}
                style={{
                    background: `${Appearance.colors.textField()} url("images/custom-select-list-arrows-${window.theme === 'dark' ? 'white' : 'black'}.png") no-repeat right 0.75rem center`,
                    backgroundSize: '10px',
                    marginRight: 8
                }}>
                    {timezones.current.map((tz, index) => {
                        return <option key={index} id={tz}>{tz}</option>
                    })}
                </select>
            </div>
        </div>
    )
}
export default DateDurationPickerField;
